/* eslint-disable jsdoc/check-param-names */
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  DestroyRef,
  type WritableSignal,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  Inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import FuzzySearch from 'fuzzy-search';
import { distinctUntilChanged } from 'rxjs';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirInputComponent } from '../input/input.component';
import { PalantirModalComponent } from '../modal/modal.component';
import { PalantirTabsModule } from '../tabs/tabs.module';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirToolbarComponent } from '../toolbar/toolbar.component';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import { PalantirFilterModalDateInputComponent } from './components/inputs/date/date.component';
import { PalantirFilterModalDateRangeInputComponent } from './components/inputs/date-range/date-range.component';
import { PalantirFilterModalMultiSelectInputComponent } from './components/inputs/multi-select/multi-select.component';
import { PalantirFilterModalSelectInputComponent } from './components/inputs/radio/radio.component';
import { PalantirFilterModalSearchInputComponent } from './components/inputs/search/search.component';
import { PalantirFilterModalTextInputComponent } from './components/inputs/text/text.component';
import type { PalantirFilterModalInstance } from './filter-modal.instance';
import { PalantirFilterModalService } from './filter-modal.service';
import type { PalantirFilterInput, PalantirFilterInputFormType } from './filter-modal.type';
import { filterModalLogger } from './logger.instance';

const logger = filterModalLogger.extend('component');

@Component({
  selector: 'palantir-filter-modal-component',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PalantirModalComponent,
    PalantirButtonComponent,
    PalantirToolbarComponent,
    PalantirTooltipModule,
    PalantirFilterModalTextInputComponent,
    PalantirFilterModalSelectInputComponent,
    PalantirFilterModalDateInputComponent,
    PalantirFilterModalDateRangeInputComponent,
    PalantirFilterModalSearchInputComponent,
    PalantirFilterModalMultiSelectInputComponent,
    PalantirTabsModule,
    PalantirInputComponent,
    PalantirIconComponent,
    PalantirTextComponent,
  ],
  templateUrl: './filter-modal.component.html',
  styleUrl: './filter-modal.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirFilterModalComponent {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public instance: PalantirFilterModalInstance<{}>;

  public searchControl: FormControl = new FormControl<string>('');

  private searchEngine!: FuzzySearch<{
    key: string;
    value: PalantirFilterInput;
  }>;

  public filteredOptions: WritableSignal<{ key: string; value: PalantirFilterInput }[]> = signal(
    [],
  );

  public filtersEntries: { key: string; value: PalantirFilterInput }[] = [];

  public constructor(
    private readonly palantirFilterModalService: PalantirFilterModalService,
    private readonly destroyRef: DestroyRef,
    @Inject(DIALOG_DATA) public modalData: { id: string },
  ) {
    const instance = this.palantirFilterModalService.getInstance(modalData.id);

    if (!instance) {
      throw new Error(
        `PalantirFilterModalComponent: Instance with id "${modalData.id}" not found.`,
      );
    }

    this.instance = instance;

    this.filtersEntries = Array.from(Object.entries(this.instance.metadata.filters)).map(
      ([key, value]) => ({
        key,
        value: value as PalantirFilterInput,
      }),
    );

    logger.debug('Initialized component for instance:', modalData.id, {
      filtersCount: this.filtersEntries.length,
    });

    this.initSearchEngine();
    this.initKeyboardNavigation();
  }

  @HostListener('window:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    // Escape key - close modal
    if (event.key === 'Escape') {
      logger.debug('Escape key pressed, closing modal');
      event.preventDefault();
      this.close();
      return;
    }

    // Ctrl/Cmd + K - clear filters
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      logger.debug('Ctrl/Cmd + K pressed, clearing filters');
      event.preventDefault();
      if (this.instance.isFiltering()) {
        this.clearFilters();
      }
    }
  }

  private initKeyboardNavigation(): void {}

  private initSearchEngine(): void {
    this.searchEngine = new FuzzySearch(this.filtersEntries, ['value.label', 'key'], {
      caseSensitive: false,
      sort: true,
    });

    this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleSearchChange(value));

    this.filteredOptions.set(this.filtersEntries);
  }

  private handleSearchChange(value: string): void {
    logger.debug('Search query changed:', value);

    if (!value) {
      this.filteredOptions.set(this.filtersEntries);
      return;
    }

    const results = this.searchEngine.search(value);
    logger.debug('Search results:', results.length);
    this.filteredOptions.set(results);
  }

  /**
   * @param _T - Esse parâmetro é utilizado para inferir o tipo do retorno, não é utilizado para nada
   */
  public getFormControl<T extends PalantirFilterInput['type']>(
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _: T,
  ): PalantirFilterInputFormType[T] {
    return this.instance.formGroup.get(key) as PalantirFilterInputFormType[T];
  }

  public clearFilters(): void {
    logger.log('Clearing all filters from UI');
    this.instance.clearFilters();
  }

  public close(): void {
    logger.log('Closing modal');
    this.instance.closeModal();
  }
}
